<template>
  <el-card shadow="never" :class="{ 'tech-card': tech }">
    <template v-if="!tech" #header>
      <CardTitle
        :title="
          shopCtx.selfScope
            ? shopCtx.tradeScopeHint
              ? `运营数据（订单待办按${shopCtx.tradeScopeHint}；商品/充值等为租户汇总）`
              : '运营数据（租户汇总）'
            : '运营数据'
        "
      />
    </template>
    <div class="flex flex-row flex-wrap items-center gap-8 p-4" :class="{ 'tech-grid': tech }">
      <div
        v-for="item in displayItems"
        :key="item.name"
        class="h-20 w-20% flex flex-col items-center justify-center gap-2"
        :class="navigate ? 'cursor-pointer' : 'cursor-default'"
        @click="handleClick(item.routerName)"
      >
        <CountTo
          :decimals="item.decimals"
          :end-val="item.value"
          :prefix="item.prefix"
          class="text-3xl"
          :class="{ 'tech-value': tech }"
        />
        <span class="text-center" :class="{ 'tech-label': tech }">{{ item.name }}</span>
      </div>
    </div>
  </el-card>
</template>
<script lang="ts" setup>
import * as ProductSpuApi from '@/api/mall/product/spu'
import * as TradeStatisticsApi from '@/api/mall/statistics/trade'
import * as PayStatisticsApi from '@/api/mall/statistics/pay'
import { CardTitle } from '@/components/Card'
import { fenToYuan } from '@/utils'
import { useMallSelfShopContext } from '@/hooks/web/useMallSelfShopContext'
import { computed, reactive, ref } from 'vue'

/** 运营数据卡片 */
defineOptions({ name: 'OperationDataCard' })

const props = withDefaults(
  defineProps<{
    tech?: boolean
    /** 纯净驾驶舱窗口内禁用跳转（路由未初始化） */
    navigate?: boolean
  }>(),
  { tech: false, navigate: true }
)

const router = useRouter()
const { context: shopCtx, buildShopFilter, refresh: refreshShopCtx } = useMallSelfShopContext()
type OperationDataItem = {
  name: string
  value: number
  routerName: string
  prefix?: string
  decimals?: number
}

const data = reactive<Record<string, OperationDataItem>>({
  orderUndelivered: { name: '待发货订单', value: 9, routerName: 'TradeOrder' },
  orderAfterSaleApply: { name: '退款中订单', value: 4, routerName: 'TradeAfterSale' },
  orderWaitePickUp: { name: '待核销订单', value: 0, routerName: 'TradeOrder' },
  productAlertStock: { name: '库存预警', value: 0, routerName: 'ProductSpu' },
  productForSale: { name: '上架商品', value: 0, routerName: 'ProductSpu' },
  productInWarehouse: { name: '仓库商品', value: 0, routerName: 'ProductSpu' },
  withdrawAuditing: { name: '提现待审核', value: 0, routerName: 'TradeBrokerageWithdraw' },
  rechargePrice: {
    name: '账户充值',
    value: 0.0,
    prefix: '￥',
    decimals: 2,
    routerName: 'PayWalletRecharge'
  }
})

const hideWithdraw = ref(false)
const displayItems = computed(() =>
  Object.values(data).filter(
    (item) => !(hideWithdraw.value && item.routerName === 'TradeBrokerageWithdraw')
  )
)

const getOrderData = async () => {
  await refreshShopCtx()
  const orderCount = await TradeStatisticsApi.getOrderCount(buildShopFilter())
  if (orderCount.undelivered != null) {
    data.orderUndelivered.value = orderCount.undelivered
  }
  if (orderCount.afterSaleApply != null) {
    data.orderAfterSaleApply.value = orderCount.afterSaleApply
  }
  if (orderCount.pickUp != null) {
    data.orderWaitePickUp.value = orderCount.pickUp
  }
  if (orderCount.auditingWithdraw != null) {
    data.withdrawAuditing.value = orderCount.auditingWithdraw
    hideWithdraw.value = false
  } else {
    hideWithdraw.value = true
  }
}

const getProductData = async () => {
  const productCount = await ProductSpuApi.getTabsCount()
  data.productForSale.value = productCount['0']
  data.productInWarehouse.value = productCount['1']
  data.productAlertStock.value = productCount['3']
}

const getWalletRechargeData = async () => {
  const paySummary = await PayStatisticsApi.getWalletRechargePrice()
  data.rechargePrice.value = Number(fenToYuan(paySummary.rechargePrice || 0))
}

const handleClick = (routerName: string) => {
  if (!props.navigate) return
  router.push({ name: routerName })
}

onActivated(() => {
  getOrderData()
  getProductData()
  getWalletRechargeData()
})

onMounted(() => {
  getOrderData()
  getProductData()
  getWalletRechargeData()
})

watch(
  () => shopCtx.value.selfScope,
  () => {
    void getOrderData()
  }
)
</script>

<style scoped lang="scss">
.tech-card {
  color: #e6f7ff;
  background: transparent;
  border: none;
}

.tech-grid {
  gap: 12px !important;
  padding: 8px 4px 12px !important;
}

.tech-value {
  color: #00f2fe !important;
  text-shadow: 0 0 10px rgba(0, 242, 254, 0.35);
}

.tech-label {
  color: #9fc4ee;
  font-size: 12px;
}
</style>
